import { ListViewFilter, ReturnFields } from '@crm/api'
import { ViewFiltersFieldValue } from '@crm/features/leads/components'
import { fieldTypeOperators, FilterOperatorType, Properties } from '@crm/types'
import { useLocales } from '@shared/locales'
import { Button, Col, Form, FormInstance, Row, Select, Space } from 'antd'
import { Add, Trash } from 'iconsax-react'
import { Dispatch, SetStateAction, useCallback } from 'react'

type Props = {
  filters: ListViewFilter[]
  dataFields: ReturnFields | undefined
  form: FormInstance
  fieldsFilter?: Properties
  setFilters: Dispatch<SetStateAction<ListViewFilter[]>>
  setFieldsFilter?: (value: Properties) => void
}

export const FilterSet = ({
  filters,
  dataFields,
  form,
  fieldsFilter,
  setFilters,
  setFieldsFilter
}: Props) => {
  const { t } = useLocales()

  const findViewField = useCallback(
    (i: number) =>
      dataFields?.fields.find(
        (field) => filters && filters[i]?.field_name === field.filter_name
      ),
    [filters, dataFields?.fields]
  )

  const onSearch = (search: string) => {
    setFieldsFilter?.({ search, page: 1 })
  }

  return (
    <>
      {filters?.map(
        (filter, i) =>
          filter && (
            <Row gutter={8} key={i}>
              <Col span={6}>
                <Form.Item
                  name={['filters', i, 'field_name']}
                  rules={[
                    { required: true, message: t('views.name_is_required') }
                  ]}
                >
                  <Select
                    showSearch
                    value={fieldsFilter?.search}
                    onSearch={onSearch}
                    options={dataFields?.fields?.map((item) => ({
                      value: item?.filter_name,
                      label: item?.label
                    }))}
                  />
                </Form.Item>
              </Col>

              <Col span={6}>
                <Form.Item
                  name={['filters', i, 'filter_operator']}
                  rules={[
                    {
                      required: !!filters[i]?.field_name,
                      message: t('views.operator_is_required')
                    }
                  ]}
                >
                  <Select
                    options={
                      findViewField(i)?.type === undefined
                        ? Object.values(FilterOperatorType)?.map((item) => ({
                            value: item,
                            label: t(`operators.${item}`)
                          }))
                        : fieldTypeOperators[
                            (findViewField(i)?.type ||
                              '') as keyof typeof fieldTypeOperators
                          ].map((item) => ({
                            value: item,
                            label: t(`operators.${item}`)
                          }))
                    }
                    disabled={!filters[i]?.field_name}
                  />
                </Form.Item>
              </Col>

              <Col span={8}>
                <Space size="small" align="start">
                  <Form.Item
                    name={['filters', i, 'value']}
                    rules={[
                      {
                        required: !!filters[i]?.field_name,
                        message: t('views.value_is_required')
                      }
                    ]}
                  >
                    <ViewFiltersFieldValue
                      viewsFields={dataFields}
                      numberOfElement={i}
                      filters={filters}
                      isDisabled={!filters[i]?.filter_operator}
                    />
                  </Form.Item>

                  {filters?.length > 1 && (
                    <Button
                      icon={<Trash size={16} />}
                      onClick={() => {
                        let values = filters
                        setFilters((prevState) => {
                          const value = prevState.filter((_, y) => y !== i)
                          values = value
                          return value
                        })
                        form.setFieldsValue({
                          filters: values
                        })
                      }}
                    />
                  )}
                </Space>
              </Col>
            </Row>
          )
      )}

      <Button
        type="text"
        icon={<Add size={16} />}
        onClick={() => {
          setFilters((prevState) => {
            return [
              ...prevState,
              {
                value: [],
                field_name: '',
                filter_operator: FilterOperatorType.EQUAL,
                order: prevState.length
              }
            ]
          })
        }}
      >
        {t('generic.add_filter')}
      </Button>
    </>
  )
}
